import { Box } from '@mui/material';
import { AppointmentRequestSourceFilter } from 'apps/wrapper/src/services/appointment/types/appointment-request-source-filter';
import { AppointmentRequestStatusFilter } from 'apps/wrapper/src/services/appointment/types/appointment-request-status-filter';
import { AppointmentRequestTypeFilter } from 'apps/wrapper/src/services/appointment/types/appointment-request-type-filter';
import { FC } from 'react';
import { AppointmentRequestsFilter } from '../../../../../../types/appointment-request/appointment-requests-filter';
import { useApptRequestsWidget } from '../../hooks/use-appt-requests-widget';
import { SelectedFilterChip } from './filters.styles';

const getStatusLabel = (status: AppointmentRequestStatusFilter): string => {
  switch (status) {
    case AppointmentRequestStatusFilter.Pending:
      return 'Pending';
    case AppointmentRequestStatusFilter.Approved:
      return 'Approved';
    case AppointmentRequestStatusFilter.Dismissed:
      return 'Dismissed';
    default:
      return status;
  }
};

const getTypeLabel = (type: AppointmentRequestTypeFilter): string => {
  switch (type) {
    case AppointmentRequestTypeFilter.AppointmentRequests:
      return 'Appointment Requests';
    case AppointmentRequestTypeFilter.Rescheduling:
      return 'Rescheduling';
    case AppointmentRequestTypeFilter.Cancellation:
      return 'Cancellation';
    case AppointmentRequestTypeFilter.DirectScheduled:
      return 'Directly Scheduled';
    default:
      return type;
  }
};

const getSourceLabel = (source: AppointmentRequestSourceFilter): string => {
  switch (source) {
    case AppointmentRequestSourceFilter.AI:
      return 'RW AI';
    case AppointmentRequestSourceFilter.AppointmentForm:
      return 'Appointment Form';
    case AppointmentRequestSourceFilter.WebsiteWidget:
      return 'Website Widget';
    default:
      return source;
  }
};

export const AppliedFilters: FC = () => {
  const { appointmentRequestsFilter, setAppointmentRequestsFilter } = useApptRequestsWidget();

  // Early return if filter is not initialized yet
  if (!appointmentRequestsFilter) return null;

  const updateFilter = async (value: Partial<AppointmentRequestsFilter>) => {
    setAppointmentRequestsFilter({ ...appointmentRequestsFilter, ...value });
  };

  const removeStatusFilter = (statusToRemove: AppointmentRequestStatusFilter) => {
    const newStatus = appointmentRequestsFilter.requestStatus.filter((status) => status !== statusToRemove);
    updateFilter({ requestStatus: newStatus });
  };

  const removeTypeFilter = (typeToRemove: AppointmentRequestTypeFilter) => {
    const newType = appointmentRequestsFilter.requestType.filter((type) => type !== typeToRemove);
    updateFilter({ requestType: newType });
  };

  const removeSourceFilter = (sourceToRemove: AppointmentRequestSourceFilter) => {
    const newSource = appointmentRequestsFilter.source.filter((source) => source !== sourceToRemove);
    updateFilter({ source: newSource });
  };

  const hasNonDefaultFilters = () => {
    const hasStatusFilters =
      appointmentRequestsFilter.requestStatus.length > 0 &&
      !(
        appointmentRequestsFilter.requestStatus.length === 1 &&
        appointmentRequestsFilter.requestStatus[0] === AppointmentRequestStatusFilter.All
      );

    const hasTypeFilters =
      appointmentRequestsFilter.requestType.length > 0 &&
      !(
        appointmentRequestsFilter.requestType.length === 1 &&
        appointmentRequestsFilter.requestType[0] === AppointmentRequestTypeFilter.All
      );

    const hasSourceFilters =
      appointmentRequestsFilter.source.length > 0 &&
      !(
        appointmentRequestsFilter.source.length === 1 &&
        appointmentRequestsFilter.source[0] === AppointmentRequestSourceFilter.All
      );

    return hasStatusFilters || hasTypeFilters || hasSourceFilters;
  };

  if (!hasNonDefaultFilters()) return null;

  return (
    <Box sx={{ marginTop: '15px', width: '100%' }}>
      {appointmentRequestsFilter.requestStatus
        .filter((status) => status !== AppointmentRequestStatusFilter.All)
        .map((status) => (
          <SelectedFilterChip
            key={status}
            label={`Status: ${getStatusLabel(status)}`}
            onDelete={() => removeStatusFilter(status)}
          />
        ))}

      {appointmentRequestsFilter.requestType
        .filter((type) => type !== AppointmentRequestTypeFilter.All)
        .map((type) => (
          <SelectedFilterChip
            key={type}
            label={`Type: ${getTypeLabel(type)}`}
            onDelete={() => removeTypeFilter(type)}
          />
        ))}

      {appointmentRequestsFilter.source
        .filter((source) => source !== AppointmentRequestSourceFilter.All)
        .map((source) => (
          <SelectedFilterChip
            key={source}
            label={`Source: ${getSourceLabel(source)}`}
            onDelete={() => removeSourceFilter(source)}
          />
        ))}
    </Box>
  );
};
